/**
 * Ayarlar — the reference's `data-screen="settings"` (`Screens.settings`, `m08b-admin2.js:322`).
 *
 * One screen, two tabs, two routes: `/admin/settings/email` builds the confirmation e-mail beside a
 * live preview of it, and `/admin/settings/verification` holds the issuer identity, the two LinkedIn
 * cards, the public privacy switches, the claim policy and the sender-domain readiness band.
 *
 * Three reference behaviours are reproduced rather than tidied up, because each of them is a rule
 * rather than an oversight:
 *
 * — The preview's subject and body come from the sample credential's own immutable release, not from
 *   the four «Konu / Metin» boxes above it. Editing a box repaints the preview (the sender block
 *   follows `senderName`) but cannot rewrite a released template; see `lib/settings-email.ts`.
 * — B2's API access status is `not_applied` and the card says NOT APPLIED out loud. The simulation
 *   switch opens prototype screens only and never claims real access (binding rules 14 and 15).
 * — The privacy list is mostly disabled: those are invariants of the product, not preferences, so
 *   the reference shows them as checked-and-locked instead of hiding them.
 */
import {
  type ChangeEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { SheetSyncStatusView } from '../data/commands';
import { useV07Commands } from '../data/commands-context';
import { useV07Role } from '../data/session';
import { useDemoData } from '../data/store';
import type { DemoCredential, DemoDatabase } from '../data/types';
import { ingestImageFile, seedAssets } from '../lib/asset-library';
import {
  CLAIM_EMAIL_BODY_HINT,
  CLAIM_EMAIL_STORED_RULE,
  CLAIM_EMAIL_SUBJECT_HINT,
} from '../lib/claim-email-default';
import {
  CREDENTIAL_LINKEDIN_SYNC_SHAPE,
  disableLinkedInSimulation,
  disconnectLinkedInMember,
  enableLinkedInSimulation,
  expireLinkedInToken,
  isLinkedInHost,
  LINKEDIN_ADD_TO_PROFILE_URL,
  LINKEDIN_CONTRACT_RULES,
  LINKEDIN_MEMBER_CONNECTION_SHAPE,
  LINKEDIN_ORGANIZATION_REFERENCE,
  LINKEDIN_REFERENCES,
  LINKEDIN_STATUS_TR,
  linkedInPillClass,
  linkedInState,
  revokeLinkedInPermission,
  subscribeLinkedIn,
} from '../lib/linkedin-integration';
import { verifiedOrganizationIdForPage } from '../lib/linkedin-organization';
import { fmtDateTime, fmtNum } from '../lib/reference';
import { credentialSVG } from '../lib/render';
import { emailBodyLines, renderEmail, testEmails } from '../lib/settings-email';
import { useToast } from '../ui/toast';
import { SettingsUsersScreen } from './SettingsUsersScreen';

/**
 * `ICON.ext` — `m01-utils.js:312`, with the one thing the reference's own glyph never carried: a size.
 *
 * An `<svg>` with a `viewBox` and no `width`/`height` is a replaced element with a ratio and NO
 * intrinsic size, so it takes the whole width its container offers and the ratio gives it the
 * height. Measured in a real browser on `/admin/settings/verification`, the three reference links
 * each drew this glyph at **461 x 461** CSS px at 1440 and **258 x 258** at 390 — three squares
 * taller than the card they sit in. The two copies inside `.btn` looked right for an unrelated
 * reason: `prototype.css:125` sizes `.btn svg`, and nothing sizes an `<svg>` inside a plain `<a>`.
 *
 * The size therefore belongs to the glyph, not to a stylesheet rule about `svg`: a blanket
 * `svg { width: 16px }` would reach the certificate, the badge, the QR code, the issuer logo and
 * the LinkedIn share canvas, every one of which draws at its own size on purpose. `16` matches
 * what `.btn svg` already asks for, so the two button copies are unchanged.
 */
export function ExtIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

const str = (source: Record<string, unknown>, key: string): string =>
  typeof source[key] === 'string' ? (source[key] as string) : '';

const bool = (source: Record<string, unknown>, key: string): boolean => source[key] === true;

// ------------------------------------------------------------------------------ E-posta şablonu

const PLACEHOLDER_KEYS = [
  'holder_name',
  'recipient_name',
  'organization_name',
  'credential_title',
  'credential_code',
  'credential_url',
  'claim_url',
  'issue_date',
  'expiry_date',
  'certification_period',
  'issuer_name',
];

function EmailPreview({ db, c }: { db: DemoDatabase; c: DemoCredential }): ReactElement {
  const em = renderEmail(db, c);
  const senderName = str(db.settings.email as Record<string, unknown>, 'senderName');
  return (
    <div className="email-body" lang={em.lang}>
      <div className="brand-line">
        <i />
        {senderName}
      </div>
      <div className="small muted" style={{ marginBottom: '14px' }}>
        {`${em.lang === 'en' ? 'Subject' : 'Konu'}: ${em.subject}`}
      </div>
      <h1>{em.heading}</h1>
      {emailBodyLines(em.body).map((line, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: body lines are a static, never-reordered list
        <p key={i} style={{ marginBottom: '10px' }}>
          {line}
        </p>
      ))}
      {/* The reference drops the watermark here: a revoked sample would otherwise stamp the mail. */}
      <div
        className="visual"
        dangerouslySetInnerHTML={{
          __html: credentialSVG(c, { lang: em.lang, noWatermark: true }),
        }}
      />
      <a className="cta" href={em.ctaHash ?? ''}>
        {em.cta}
      </a>
      <p className="small muted">
        {em.lang === 'en'
          ? 'Nothing is published until you accept.'
          : 'Onaylamadan hiçbir bilginiz yayınlanmaz.'}
      </p>
      <div className="foot">
        {`${c.release.issuerProfileSnapshot.displayName} · ${c.privateContactEmail ?? ''}`}
      </div>
    </div>
  );
}

/**
 * The two stored confirmation e-mails, keyed the way the backend keys them (matrix C4). The
 * prototype edits a person's text and an organization's text; they are two rows, not one row with
 * two columns, so a deployment can carry either.
 */
const templateKey = (organization: boolean): string =>
  organization ? 'claim_invite_org_v1' : 'claim_invite_v1';

function EmailTab(): ReactElement {
  const db = useDemoData();
  const commands = useV07Commands();
  const role = useV07Role();
  const toast = useToast();
  /*
   * `issuer.update` is OWNER-ONLY in the matrix (A·D·D·D), and both writing controls on this tab
   * go through it: saving the two stored templates, and the test send that previews one. A lesser
   * role reads the tab and is offered neither. `role === null` is the demo, which sees the
   * reference's own screen.
   */
  const mayWrite = role === null || role === 'owner';
  const [previewType, setPreviewType] = useState<'person' | 'org'>('person');

  const E = db.settings.email as Record<string, unknown>;
  /*
   * Typing is a local edit; «Kaydet» is the write. The reference behaves the same way, and a screen
   * that posted on every keystroke would write a half-typed subject line into a live template.
   */
  const edit = (key: string, value: string): void =>
    commands.patchLocal((d) => {
      (d.settings.email as Record<string, unknown>)[key] = value;
    });
  const field = (key: string): { value: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void } => ({
    value: str(E, key),
    onChange: (e) => edit(key, e.target.value),
  });
  const area = (key: string): { value: string; onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void } => ({
    value: str(E, key),
    onChange: (e) => edit(key, e.target.value),
  });

  /* `sampleCred` — an unpublished, unrevoked record of the wanted type, else the first of that
     type, else anything at all. The org side of the seed has only published records, so «Şirket»
     legitimately previews a live one. */
  const sample = useMemo<DemoCredential | null>(() => {
    const want = previewType === 'org' ? 'organization' : 'person';
    const list = db.credentials.filter(
      (c) => (c.subjectSnapshot as { subjectType?: string }).subjectType === want,
    );
    return list.find((c) => !c.publishedAt && !c.revokedAt) ?? list[0] ?? db.credentials[0] ?? null;
  }, [db.credentials, previewType]);

  const log = testEmails(db);
  const logLine =
    log.length > 0
      ? `Test kayıtları: ${log
          .slice(-3)
          .map((x) => `${fmtDateTime(x.at)} → ${x.to} · «${x.subject}»`)
          .join(' · ')} (simülasyon)`
      : '';

  /*
   * The test send has no recipient field, by design: it goes to the signed-in user's own verified
   * address and nowhere else. A settings screen that can mail an arbitrary address is a spam relay
   * with a login page.
   */
  const sendTest = async (): Promise<void> => {
    if (sample === null) {
      toast('Örnek kayıt yok');
      return;
    }
    const org = (sample.subjectSnapshot as { subjectType?: string }).subjectType === 'organization';
    const r = await commands.sendEmailTemplateTest({ key: templateKey(org), lang: 'tr' });
    toast(
      r.ok
        ? `Test e-postası gönderildi${r.message ? ` → ${r.message}` : ''}`
        : (r.message ?? 'Test e-postası gönderilemedi'),
    );
  };

  /*
   * «Kaydet» writes the two texts the prototype edits — the person's and the organization's — each
   * to its own stored template. The sender identity above it (ad, adres, yanıt adresi) has no
   * store in any canonical contract yet (matrix H9), so the button says what it did and what it
   * could not do, rather than reporting a save that did not happen.
   */
  const save = async (): Promise<void> => {
    /*
     * A text that has not been written is not saved. The prototype ships both the person's and the
     * organization's words filled in; a deployment starts with neither, and refusing the whole tab
     * because the half nobody has typed yet is empty would make the button unusable for the one
     * half that was.
     */
    const pending = [
      { key: templateKey(false), subject: str(E, 'subjectPerson'), body: str(E, 'bodyPerson') },
      { key: templateKey(true), subject: str(E, 'subjectOrg'), body: str(E, 'bodyOrg') },
    ].filter((t) => t.subject.trim() !== '' && t.body.trim() !== '');

    if (pending.length === 0) {
      toast('Kaydedilecek bir metin yok: konu ve gövdeyi doldurun');
      return;
    }

    const results = await Promise.all(
      pending.map((t) =>
        commands.saveEmailTemplate({
          key: t.key,
          lang: 'tr',
          subject: t.subject,
          bodyHtml: t.body,
          bodyText: t.body,
        }),
      ),
    );
    const failed = results.find((r) => !r.ok);
    toast(
      failed
        ? (failed.message ?? 'E-posta metni kaydedilemedi')
        : `${pending.length === 1 ? 'E-posta metni' : 'E-posta metinleri'} kaydedildi — gönderen kimliği için henüz bir ayar deposu yok (H9)`,
    );
  };

  return (
    <div className="split">
      <div className="card">
        <div className="card-head">
          <h2>Onay e-postası</h2>
          <span className="small muted">{'Resend · ek dosya yok · ana bağlantı claim URL'}</span>
        </div>
        <div className="card-pad stack" style={{ gap: '14px' }}>
          <div className="field-row">
            <div className="field">
              <label htmlFor="em_senderName">Gönderen adı</label>
              <input className="input" id="em_senderName" data-s="senderName" {...field('senderName')} />
            </div>
            <div className="field">
              <label htmlFor="em_fromEmail">Gönderen adresi</label>
              <input className="input" id="em_fromEmail" data-s="fromEmail" {...field('fromEmail')} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="em_replyTo">{'Yanıt adresi (isteğe bağlı)'}</label>
            <input
              className="input"
              id="em_replyTo"
              data-s="replyTo"
              placeholder="boş bırakılabilir"
              {...field('replyTo')}
            />
          </div>
          {/*
           * The boxes carry the approved default as a HINT, never as a value.
           *
           * They were blank, and a blank box says nothing about what is actually sent. Leaving them
           * alone sends the approved message; the hint says so, and the note below says what
           * writing your own costs. Nothing is stored until the operator types something.
           */}
          <div className="field">
            <label htmlFor="em_subjectPerson">{'Konu — kişi (rozet / sertifika)'}</label>
            <input
              className="input"
              id="em_subjectPerson"
              data-s="subjectPerson"
              placeholder={CLAIM_EMAIL_SUBJECT_HINT}
              {...field('subjectPerson')}
            />
          </div>
          <div className="field">
            <label htmlFor="em_bodyPerson">{'Metin — kişi'}</label>
            <textarea
              className="textarea"
              id="em_bodyPerson"
              data-s="bodyPerson"
              style={{ minHeight: '150px' }}
              placeholder={CLAIM_EMAIL_BODY_HINT}
              {...area('bodyPerson')}
            />
          </div>
          <div className="field">
            <label htmlFor="em_subjectOrg">{'Konu — şirket sertifikası'}</label>
            <input
              className="input"
              id="em_subjectOrg"
              data-s="subjectOrg"
              placeholder={CLAIM_EMAIL_SUBJECT_HINT}
              {...field('subjectOrg')}
            />
          </div>
          <div className="field">
            <label htmlFor="em_bodyOrg">{'Metin — şirket sertifikası'}</label>
            <textarea
              className="textarea"
              id="em_bodyOrg"
              data-s="bodyOrg"
              style={{ minHeight: '150px' }}
              placeholder={CLAIM_EMAIL_BODY_HINT}
              {...area('bodyOrg')}
            />
            {/* Beside the last box rather than under the group: the note is about what typing in
                any of these four costs, and this is where an operator stops reading them. */}
            <p className="help" id="em_defaultRule">
              {CLAIM_EMAIL_STORED_RULE}
            </p>
          </div>
          <div>
            <div className="label" style={{ marginBottom: '6px' }}>
              Kullanılabilir alanlar
            </div>
            <div className="placeholder-list">
              {PLACEHOLDER_KEYS.map((k) => (
                <code key={k}>{`{{${k}}}`}</code>
              ))}
            </div>
            <p className="help" style={{ marginTop: '6px' }}>
              <code>holder_name</code>
              {
                ' kişi kaydında alıcı adı, şirket kaydında şirket adıdır; başlık («Rozetiniz hazır.» / «Şirket sertifikanız hazır.») ve ana düğme türe göre otomatik gelir. Ana düğme onay bekleyen kayıtta claim bağlantısına, yayınlanmış kayıtta public sayfaya gider.'
              }
            </p>
          </div>
          <div className="info-box">
            {
              "Akış: Kayıt oluşturuldu → Onay e-postası gönderildi → Alıcı claim sayfasını açtı → Alıcı onayladı → Public sayfa aktif oldu. E-posta credential'ın kendisi değil, onay sayfasına açılan kapıdır; ek dosya gönderilmez."
            }
          </div>
        </div>
        <div className="card-foot">
          {mayWrite ? (
            <>
              <button className="btn btn-secondary" type="button" id="emTest" onClick={() => void sendTest()}>
                Test e-postası gönder
              </button>
              <button className="btn btn-primary" type="button" id="emSave" onClick={() => void save()}>
                Kaydet
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <h2>Önizleme</h2>
          {/* biome-ignore lint/a11y/useSemanticElements: the reference's chip row is a div with role="group"; its markup is the acceptance target */}
          <div className="chips" role="group" aria-label="Önizleme türü">
            <button
              className={previewType === 'person' ? 'chip is-on' : 'chip'}
              type="button"
              data-pt="person"
              aria-pressed={previewType === 'person'}
              onClick={() => setPreviewType('person')}
            >
              Kişi
            </button>
            <button
              className={previewType === 'org' ? 'chip is-on' : 'chip'}
              type="button"
              data-pt="org"
              aria-pressed={previewType === 'org'}
              onClick={() => setPreviewType('org')}
            >
              Şirket
            </button>
          </div>
        </div>
        <div className="card-pad">
          <div className="email-frame" id="emailPreview">
            {sample === null ? (
              <p className="muted">Örnek kayıt yok.</p>
            ) : (
              <EmailPreview db={db} c={sample} />
            )}
          </div>
          <div id="emTestLog" className="help" style={{ marginTop: '8px' }}>
            {logLine}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------- Kurum kimliği, LinkedIn, gizlilik & claim

type SettingsTextKey =
  | 'issuerDisplayName'
  | 'linkedinIssuerName'
  | 'issuerWebsite'
  | 'linkedinPageUrl'
  | 'publicBase'
  | 'supportEmail';

/** `ok(s)` — three states, three pills; anything that is not ok or warn reads as missing. */
function ReadinessPill({ state }: { state: string }): ReactElement {
  if (state === 'ok') return <span className="pill pill-active">✓ Hazır</span>;
  if (state === 'warn') return <span className="pill pill-expired">⚠ Eksik</span>;
  return <span className="pill pill-danger">✕ Yok</span>;
}

function StatusPill({ value }: { value: string }): ReactElement {
  return <span className={`pill ${linkedInPillClass(value)}`}>{LINKEDIN_STATUS_TR[value] ?? value}</span>;
}

/**
 * What a Google refusal means, said in the product's own words.
 *
 * The server sends a code and never a provider message — a Google error body can carry a URL with a
 * token in it — so the sentence a person reads is written here, from the code alone, and names the
 * thing they can actually go and fix.
 */
const SHEET_ERROR_TR: Record<string, string> = {
  sheets_not_configured: 'Google erişimi bu dağıtımda yapılandırılmadı (yapılandırma bekliyor).',
  sheets_auth_failed:
    'Google erişimi reddetti. Servis hesabı anahtarı geçersiz veya dosya bu hesapla paylaşılmamış.',
  sheets_file_unavailable:
    'E-tablo veya sayfa bulunamadı. Kimlik yanlış olabilir ya da dosya servis hesabıyla paylaşılmamış.',
  sheets_unavailable: 'Google şu anda yanıt vermiyor. Sonraki denemede yeniden yazılacak.',
  sheets_result_unknown:
    'Güncelleme gönderildi ama Google’dan cevap gelmedi; sonucu belirlenemedi. E-tablonun yazılmış ya da eski hâlinde kalmış olması mümkündür. Sonraki tur sayfayı baştan yazar.',
  sheet_sync_failed: 'Güncelleme tamamlanamadı.',
};

/**
 * The one code that may NOT be followed by «the last good copy is still there».
 *
 * A refusal is an answer, and an answer means Google decided before applying anything — there the
 * previous contents really are intact. A request that was sent and never answered is neither: it
 * may have applied and it may not have, and saying either would be a guess dressed as a fact.
 */
const SHEET_ERROR_AMBIGUOUS = 'sheets_result_unknown';

/** A run that did not write, and did not fail either. */
const SKIP_TR: Record<string, string> = {
  disabled: 'Otomatik senkronizasyon kapalı ya da e-tablo seçilmemiş.',
  not_configured: 'Google erişimi yapılandırılmadı; güncelleme çalıştırılmadı.',
  locked: 'Başka bir güncelleme sürüyor; bu tur atlandı.',
};

/** The one-word state, and never a stronger word than the server's answer allows. */
function SheetSyncPill({
  status,
  loading,
  error,
}: {
  status: SheetSyncStatusView | null;
  loading: boolean;
  error: string | null;
}): ReactElement {
  if (loading) return <span className="pill pill-neutral none">Okunuyor</span>;
  if (error || !status) return <span className="pill pill-danger none">Okunamadı</span>;
  if (!status.configured) return <span className="pill pill-info none">Yapılandırma bekliyor</span>;
  if (!status.enabled) return <span className="pill pill-neutral none">Kapalı</span>;
  /*
   * «Hata» is a claim, and there is one code it would not be true of: a change that was sent and
   * never answered may have been applied. That gets its own word rather than being rounded up to a
   * failure or down to a success.
   */
  if (status.lastErrorCode === SHEET_ERROR_AMBIGUOUS)
    return <span className="pill pill-info none">Sonuç belirsiz</span>;
  if (status.lastErrorCode) return <span className="pill pill-danger none">Hata</span>;
  return <span className="pill pill-active none">Açık</span>;
}

/**
 * F · The Google Sheets mirror of the register — owner only.
 *
 * WHY IT IS DRAWN ONLY FOR AN OWNER
 * ---------------------------------
 * `integration_settings.update` is an owner-only cell and has been since v0.7.1, so this is the one
 * role that can do anything here. The backend refuses everybody else whatever the screen draws —
 * the check that matters is there — but showing an admin a switch that answers 403 is a dead
 * control, which §11 forbids. The visual-acceptance surface runs with no role at all and therefore
 * draws none of this, which is also why the frozen reference is unaffected.
 *
 * WHAT IT REFUSES TO CLAIM
 * ------------------------
 * «Son başarılı güncelleme» is the last time a WHOLE write finished. A refused credential or an
 * unreachable endpoint leaves that time where it was and shows the error instead, because a panel
 * that says «up to date» about a file that has been failing since Tuesday is worse than one that
 * says nothing. One outcome is neither: a change that was SENT and never answered may or may not
 * have been applied, so it is shown as «Sonuç belirsiz», the time does not move, and the panel does
 * not tell anybody the previous contents are safe. And where the deployment has no Google
 * credential at all, it says «yapılandırma bekliyor» rather than offering a switch that could only
 * fail.
 */
function SheetSyncCard(): ReactElement {
  const commands = useV07Commands();
  const toast = useToast();
  const [status, setStatus] = useState<SheetSyncStatusView | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'run' | null>(null);
  /* The form's own text, so a half-typed id is never written to the world. */
  const [sheetRef, setSheetRef] = useState('');
  const [title, setTitle] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [withEmail, setWithEmail] = useState(false);

  /*
   * Stable, because the effect below depends on it.
   *
   * A plain function would be a new value on every render, and an effect that depends on it would
   * re-read the settings forever. The setters React hands back are stable, so this has no deps.
   */
  const adopt = useCallback((s: SheetSyncStatusView): void => {
    setStatus(s);
    setSheetRef(s.spreadsheetId ?? '');
    setTitle(s.sheetTitle);
    setEnabled(s.enabled);
    setWithEmail(s.includeRecipientEmail);
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      const r = await commands.readSheetSync();
      if (!live) return;
      setLoading(false);
      if (r.ok && r.status) adopt(r.status);
      else setReadError(r.message ?? 'Ayarlar okunamadı.');
    })();
    return () => {
      live = false;
    };
  }, [commands, adopt]);

  const save = async (): Promise<void> => {
    setBusy('save');
    const r = await commands.saveSheetSync({
      enabled,
      spreadsheet: sheetRef,
      sheetTitle: title,
      includeRecipientEmail: withEmail,
    });
    setBusy(null);
    if (r.ok && r.status) {
      adopt(r.status);
      toast('Google e-tablo ayarları kaydedildi', 'ok');
    } else toast(r.message ?? 'Kaydedilemedi', 'danger');
  };

  const runNow = async (): Promise<void> => {
    setBusy('run');
    const r = await commands.runSheetSync();
    setBusy(null);
    if (r.status) adopt(r.status);
    if (!r.ok) {
      toast(r.message ?? 'Güncellenemedi', 'danger');
      return;
    }
    /* The RUN's own answer, not the click's: «gönderildi» is not «yazıldı». */
    if (r.run?.status === 'written') toast(`E-tablo güncellendi · ${fmtNum(r.run.rows ?? 0)} kayıt`, 'ok');
    else if (r.run?.status === 'skipped')
      toast(SKIP_TR[r.run.reason ?? ''] ?? 'Güncelleme çalıştırılmadı', 'warn');
    else toast(SHEET_ERROR_TR[r.run?.code ?? ''] ?? 'Google güncellemesi başarısız oldu', 'danger');
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>{'F · Google E-Tablo senkronizasyonu'}</h2>
        <SheetSyncPill status={status} loading={loading} error={readError} />
      </div>
      <div className="card-pad">
        <p className="help" style={{ marginTop: 0 }}>
          {
            'Kayıt defterinin tek yönlü bir raporlama kopyasıdır: yetkili kayıtların tamamı beş dakikada bir aynı Google e-tablosuna yazılır. E-tabloda yapılan değişiklikler geri okunmaz ve üründe hiçbir şeyi değiştirmez; kaynak kayıt her zaman bu uygulamadır.'
          }
        </p>

        {loading ? <div className="help">Ayarlar okunuyor…</div> : null}
        {readError ? <div className="help">{readError}</div> : null}

        {status ? (
          <>
            {!status.configured ? (
              <div className="help" id="sheetSyncWaiting">
                {
                  'Bu dağıtımda Google erişimi yapılandırılmadı — yapılandırma bekliyor. Sunucuya servis hesabı anahtarı tanımlanana kadar senkronizasyon açılamaz; e-tabloyu şimdiden seçip kaydedebilirsiniz.'
                }
              </div>
            ) : null}

            <div className="field-row" style={{ marginTop: '10px' }}>
              <div className="field">
                <label htmlFor="sheetSyncId">{'Google e-tablo kimliği veya adresi'}</label>
                <input
                  id="sheetSyncId"
                  type="text"
                  value={sheetRef}
                  onChange={(e) => setSheetRef(e.target.value)}
                  placeholder="1AbC…  ·  https://docs.google.com/spreadsheets/d/1AbC…/edit"
                />
                <div className="help">
                  {
                    'E-tablonun adresini yapıştırabilirsiniz; kimlik adresin içinden okunur. Dosya herkese açık yapılmaz — yalnız sunucunun servis hesabıyla paylaşılır.'
                  }
                </div>
              </div>
              <div className="field">
                <label htmlFor="sheetSyncTitle">{'Sayfa (tab) adı'}</label>
                <input
                  id="sheetSyncTitle"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Sertifikalar"
                />
                <div className="help">{'Yalnız bu sayfa yazılır; dosyadaki diğer sayfalara dokunulmaz.'}</div>
              </div>
            </div>

            <label className="check" style={{ marginTop: '8px' }}>
              <input
                type="checkbox"
                id="sheetSyncEnabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!status.configured}
              />
              {' Otomatik senkronizasyon açık (beş dakikada bir)'}
            </label>
            <label className="check">
              <input
                type="checkbox"
                id="sheetSyncEmail"
                checked={withEmail}
                onChange={(e) => setWithEmail(e.target.checked)}
              />
              {' Alıcı e-posta adresi sütunu da aktarılsın'}
            </label>
            <div className="help">
              {
                'Adres sütunu kapalıdır: açıldığında e-tabloyu görebilen herkes alıcıların adreslerini de görür.'
              }
            </div>

            <div className="summary-band" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: '12px' }}>
              <div className="item">
                <span>Son başarılı güncelleme</span>
                <b style={{ fontSize: '15px' }} id="sheetSyncLastSuccess">
                  {status.lastSuccessAt ? fmtDateTime(status.lastSuccessAt) : 'Henüz yok'}
                </b>
              </div>
              <div className="item">
                <span>Yazılan kayıt</span>
                <b style={{ fontSize: '15px' }}>
                  {status.lastSuccessRows === null ? '—' : fmtNum(status.lastSuccessRows)}
                </b>
              </div>
              <div className="item">
                <span>Son deneme</span>
                <b style={{ fontSize: '15px' }}>
                  {status.lastAttemptAt ? fmtDateTime(status.lastAttemptAt) : 'Henüz yok'}
                </b>
              </div>
            </div>

            {status.lastErrorCode ? (
              <div className="help" id="sheetSyncError" style={{ marginTop: '8px' }}>
                {`Son hata: ${SHEET_ERROR_TR[status.lastErrorCode] ?? status.lastErrorCode}`}
                {status.lastErrorAt ? ` · ${fmtDateTime(status.lastErrorAt)}` : ''}
                {status.lastErrorCode === SHEET_ERROR_AMBIGUOUS
                  ? ' · E-tablonun şu anki içeriği bu uçtan doğrulanamıyor.'
                  : status.lastSuccessAt
                    ? ' · E-tabloda son başarılı güncellemenin içeriği duruyor.'
                    : ' · E-tabloya henüz hiçbir tam yazma yapılmadı.'}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              {status.spreadsheetUrl ? (
                <a
                  className="btn"
                  id="sheetSyncOpen"
                  href={status.spreadsheetUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {'E-tabloyu aç'}
                </a>
              ) : null}
              <button
                className="btn"
                type="button"
                id="sheetSyncRun"
                disabled={busy !== null || !status.configured || !status.spreadsheetId}
                onClick={() => void runNow()}
              >
                {busy === 'run' ? 'Güncelleniyor…' : 'Şimdi güncelle'}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                id="sheetSyncSave"
                disabled={busy !== null}
                onClick={() => void save()}
              >
                {busy === 'save' ? 'Kaydediliyor…' : 'Ayarları kaydet'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function VerificationTab(): ReactElement {
  const db = useDemoData();
  const commands = useV07Commands();
  const role = useV07Role();
  const toast = useToast();
  /* `issuer.update` — owner only, as on the e-mail tab. */
  const mayWrite = role === null || role === 'owner';
  const li = useSyncExternalStore(subscribeLinkedIn, linkedInState);
  /*
   * This deployment's own organisation reference. `verifiedOrganizationIdForPage` answers only for
   * the LinkedIn Page it was verified against, so the number never travels to another issuer; when
   * it answers nothing the screen keeps showing the prototype's placeholder.
   */
  const issuerOrganizationId = verifiedOrganizationIdForPage(db.settings.linkedinPageUrl);
  const issuerOrganizationUrn = issuerOrganizationId
    ? `urn:li:organization:${issuerOrganizationId}`
    : LINKEDIN_ORGANIZATION_REFERENCE.urn;
  const [logoName, setLogoName] = useState<string | null>(null);

  const S = db.settings;
  /*
   * Is this installation about to get its FIRST issuer profile?
   *
   * Only when the profile was actually read and there is none. A refused read leaves the id empty
   * too, and offering «create» to somebody whose role may not even look at the profile would be
   * inviting them into a 403 (PO 7.11 §6).
   */
  const firstProfile = S.issuerProfileAccess !== 'forbidden' && !S.issuerProfileId;
  const P = S.privacy as Record<string, unknown>;
  const C = S.claim as Record<string, unknown>;
  const R = S.domainReadiness as Record<string, unknown>;

  const days = typeof C.tokenValidityDays === 'number' ? C.tokenValidityDays : 30;
  /* The field keeps the raw text so clearing it is possible; only a parsed number is committed,
     which is the reference's `parseInt(...) || previous`. */
  const [daysText, setDaysText] = useState(String(days));

  const text = (
    key: SettingsTextKey,
  ): { value: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void } => ({
    value: S[key],
    onChange: (e) =>
      commands.patchLocal((d) => {
        d.settings[key] = e.target.value;
      }),
  });

  const togglePrivacy = (key: string, checked: boolean): void => {
    commands.patchLocal((d) => {
      (d.settings.privacy as Record<string, unknown>)[key] = checked;
    });
    /* API GAP: the reference also rewrites the document's robots meta here; that tag belongs to the
       router in this build, and on the settings route the prototype forces noindex regardless. */
    toast(
      `noindex ${checked ? 'açık' : 'kapalı'} — meta etiketi güncellendi (prototip dosyası kendisi her durumda noindex kalır)`,
    );
  };

  const onLogo = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await ingestImageFile(f, seedAssets());
      commands.patchLocal((d) => {
        d.settings.issuerLogoAssetRef = r.asset.id;
      });
      /* H2: the profile records WHICH asset, never the bytes. Saved with the tab, like every
         other field on it, so one «Kaydet» is one write with one audit entry. */
      setLogoName(`${f.name} · ${r.asset.width}×${r.asset.height} · ${r.asset.sha256.slice(0, 10)}…`);
      toast('Logo kütüphaneye eklendi — kaydetmek için «Kaydet»');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    }
  };

  /*
   * «Kaydet» writes the issuer identity (matrix H1) and the logo reference (H2) — the fields that
   * have a home — and says plainly what it could not write.
   *
   * The privacy switches (H3) and the claim-link validity (H4) have no store in any canonical
   * contract, and H4 would contradict PO 7.4's «30 gün» server constant if this screen could move
   * it. Both are open Product Owner decisions. The controls stay, because §1 forbids removing a
   * prototype function for want of an API, and the button tells the truth instead of showing a
   * toast over a world that forgot.
   */
  const save = async (): Promise<void> => {
    const creating = firstProfile;
    const r = await commands.updateIssuerProfile({
      issuerDisplayName: S.issuerDisplayName,
      linkedinIssuerName: S.linkedinIssuerName,
      linkedinPageUrl: S.linkedinPageUrl || null,
      issuerWebsite: S.issuerWebsite || null,
      supportEmail: S.supportEmail || null,
      publicBase: S.publicBase,
      logoAssetId: S.issuerLogoAssetRef,
    });
    toast(
      r.ok
        ? creating
          ? 'Kurum profili oluşturuldu — şablon oluşturabilirsiniz'
          : 'Kurum kimliği kaydedildi — gizlilik ve claim süresi ayarları için henüz bir ayar deposu yok (H3, H4: Product Owner kararı)'
        : (r.message ?? (creating ? 'Kurum profili oluşturulamadı' : 'Ayarlar kaydedilemedi')),
    );
  };

  const onSimToggle = (e: ChangeEvent<HTMLInputElement>): void => {
    const on = e.target.checked;
    if (on) enableLinkedInSimulation();
    else disableLinkedInSimulation();
    toast(
      on ? 'Prototip simülasyonu açıldı (gerçek LinkedIn erişimi yok)' : 'Prototip simülasyonu kapatıldı',
    );
  };

  return (
    <div className="stack">
      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>{'A · Veren kurum kimliği'}</h2>
          </div>
          <div className="card-pad stack" style={{ gap: '12px' }}>
            <div className="field">
              <label htmlFor="st_issuerDisplayName">{'Veren kuruluş (public sayfa)'}</label>
              <input
                className="input"
                id="st_issuerDisplayName"
                data-s="issuerDisplayName"
                {...text('issuerDisplayName')}
              />
            </div>
            <div className="field">
              <label htmlFor="st_linkedinIssuerName">{"LinkedIn'de yazılacak kuruluş adı"}</label>
              <input
                className="input"
                id="st_linkedinIssuerName"
                data-s="linkedinIssuerName"
                {...text('linkedinIssuerName')}
              />
              <div className="help">
                {
                  "LinkedIn Page'in görünen adıyla birebir aynı olmalıdır; kullanıcı bu adı yazıp açılan önerilerden resmî Page'i seçer."
                }
              </div>
            </div>
            <div className="field">
              <label htmlFor="st_issuerWebsite">Resmî web sitesi</label>
              <input
                className="input"
                id="st_issuerWebsite"
                data-s="issuerWebsite"
                {...text('issuerWebsite')}
              />
            </div>
            <div className="field">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: the reference labels a file picker that lives in the row below */}
              <label>Logo</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span className="brand" style={{ padding: '0' }}>
                  <span className="mark" />
                </span>
                <label className="btn btn-secondary btn-sm" htmlFor="st_logo">
                  {'Logo yükle (PNG/SVG)'}
                </label>
                <input
                  type="file"
                  id="st_logo"
                  accept=".png,.svg,.jpg,.jpeg"
                  hidden
                  onChange={(e) => {
                    void onLogo(e);
                  }}
                />
                <span className="small muted" id="st_logoName">
                  {logoName ?? (S.issuerLogoAssetRef ? 'özel logo yüklendi' : 'marka işareti (varsayılan)')}
                </span>
              </div>
            </div>
            <div className="field">
              <label htmlFor="st_linkedinPageUrl">LinkedIn Page URL</label>
              <input
                className="input"
                id="st_linkedinPageUrl"
                data-s="linkedinPageUrl"
                {...text('linkedinPageUrl')}
              />
            </div>
            <div className="field">
              <label htmlFor="st_publicBase">Public credential domain</label>
              <input className="input" id="st_publicBase" data-s="publicBase" {...text('publicBase')} />
              <div className="help">
                {"Credential URL'si her zaman "}
                <code>{'/c/{kod}'}</code>
                {", claim URL'si "}
                <code>{'/claim/{token}'}</code>
                {" biçimindedir; dosya URL'si asla paylaşılmaz."}
              </div>
            </div>
            <div className="field">
              <label htmlFor="st_supportEmail">Destek e-posta adresi</label>
              <input className="input" id="st_supportEmail" data-s="supportEmail" {...text('supportEmail')} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{'B1 · Standart Add to Profile'}</h2>
            <span className="pill pill-active">Kullanılabilir</span>
          </div>
          <div className="card-pad stack" style={{ gap: '12px' }}>
            <p>
              {
                'LinkedIn formunu açar. LinkedIn üçüncü taraf alanlarını otomatik doldurmadığı için HPW kopyalama yardımcısı kullanılır.'
              }
            </p>
            <div className="field">
              <label htmlFor="st_li">{'Statik URL (kodda sabit, salt okunur)'}</label>
              <input className="input" id="st_li" readOnly value={LINKEDIN_ADD_TO_PROFILE_URL} />
              <div className="help">
                {'Host kontrolü: '}
                {isLinkedInHost(LINKEDIN_ADD_TO_PROFILE_URL) ? (
                  <span className="pill pill-active">✓ linkedin.com</span>
                ) : (
                  <span className="pill pill-danger">✕ geçersiz</span>
                )}
                {' · yönetici tarafından değiştirilemez.'}
              </div>
            </div>
            <div className="field">
              <label htmlFor="st_liPageRO">Resmî LinkedIn Page</label>
              <input className="input" id="st_liPageRO" readOnly value={S.linkedinPageUrl} />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <a
                className="btn btn-secondary btn-sm"
                href={LINKEDIN_ADD_TO_PROFILE_URL}
                target="_blank"
                rel="noopener"
              >
                {'LinkedIn formunu test et '}
                <ExtIcon />
              </a>
              <a
                className="btn btn-secondary btn-sm"
                href={S.linkedinPageUrl}
                target="_blank"
                rel="noopener"
                id="st_openPage"
              >
                {"LinkedIn Page'i aç "}
                <ExtIcon />
              </a>
            </div>
            <div className="warn-box">
              <b>{'Bu akış «tek tıkla otomatik ekleme» DEĞİLDİR.'}</b>
              {
                " Statik bağlantı yalnız boş Licenses & Certifications formunu açar; alanları kullanıcı LinkedIn üzerinde girer ve son Kaydet'e kendisi basar. Eski credential-specific query parametreleri (name, organizationName, issueMonth/Year, expirationMonth/Year, credentialId, credentialUrl) güncel LinkedIn deneyiminde otomatik doldurma sağlamaz ve bu üründe KULLANILMAZ."
              }
            </div>
            <div className="info-box">
              {"Yardımcıdaki «Veren kuruluş» alanı public sayfadaki addan farklıdır: LinkedIn'de "}
              <b>{S.linkedinIssuerName}</b>
              {' yazılır ve açılan önerilerden resmî Page seçilir.'}
            </div>
            <div className="warn-box">
              {
                'Yapılmayacaklar: tarayıcı eklentisi, userscript, DOM injection, clipboard makrosu, LinkedIn sayfasına kod enjeksiyonu, query-parameter prefill hilesi, kullanıcı adına otomatik kayıt veya gönderi.'
              }
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{'B2 · Profile Edit API (otomatik ekleme)'}</h2>
            <StatusPill value={li.apiAccessStatus} />
          </div>
          <div className="card-pad stack" style={{ gap: '12px' }}>
            <div className="kv-list">
              <div className="kv">
                <span className="k">API erişim durumu</span>
                <span className="v">
                  <StatusPill value={li.apiAccessStatus} /> <code>{li.apiAccessStatus}</code>
                </span>
              </div>
              <div className="kv">
                <span className="k">Üye bağlantısı</span>
                <span className="v">
                  <StatusPill value={li.memberConnectionStatus} /> <code>{li.memberConnectionStatus}</code>
                </span>
              </div>
              <div className="kv">
                <span className="k">Aktif entegrasyon modu</span>
                <span className="v">
                  <code>{li.integrationMode}</code>
                </span>
              </div>
              <div className="kv">
                <span className="k">Organization referansı</span>
                <span className="v">
                  {/*
                   * Read from the SAME verified configuration the Add-to-Profile link builder uses,
                   * and resolved against THIS deployment's own LinkedIn Page — so an issuer
                   * configured with a different Page keeps the placeholder and the unverified
                   * badge rather than inheriting another organisation's identity.
                   *
                   * The badge says only what was verified: the company id. It deliberately does
                   * not say «doğrulanmış yapılandırma», which reads as though the integration
                   * itself were approved — the Profile Edit API is still `not_applied`, and the
                   * card below this one says so.
                   */}
                  <code>{issuerOrganizationUrn}</code>{' '}
                  {issuerOrganizationId ? (
                    <>
                      <span className="pill pill-active">şirket kimliği doğrulandı</span>
                      {/*
                       * The reference's pill carried two clauses on two lines: «doğrulanmamış» and
                       * «serbest metin alanı DEĞİLDİR». Only the first became untrue. The second is
                       * still exactly what this field is, so it stays — as its own line, which is
                       * also what gives the row back the height the reference reserved for it.
                       * Without it the row loses a line and everything below shifts up.
                       */}
                      <span data-hpw-org-note>{'serbest metin alanı DEĞİLDİR'}</span>
                    </>
                  ) : (
                    <span className="pill pill-pending">
                      {'doğrulanmamış — serbest metin alanı DEĞİLDİR'}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <div className="warn-box">
              <b>NOT APPLIED.</b>
              {
                " Bu tur için LinkedIn'e gerçek bir Profile Edit API başvurusu yapılmamıştır. Erişim yalnız LinkedIn'in onayladığı geliştiricilere açıktır ve HPW'ye verileceği varsayılmaz. Gerçek başvuru durumu değişmedikçe bu alan "
              }
              <code>not_applied</code>
              {' kalır.'}
            </div>
            <label className="check">
              <input type="checkbox" id="liSimToggle" checked={li.simulated} onChange={onSimToggle} />
              {' Prototip senaryosu: «LinkedIn · Onaylı API ile Otomatik Ekleme Simülasyonu»nu aç'}
            </label>
            <div className="info-box small">
              {'Bu anahtar yalnız prototip ekranlarını açar. Açıkken bütün API ekranları kalıcı '}
              <b>PROTOTİP SİMÜLASYONU</b>
              {' etiketi taşır; hiçbir gerçek OAuth, API isteği veya profil yazması yapılmaz.'}
            </div>
            {li.simulated ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="liSimDisconnect"
                  onClick={disconnectLinkedInMember}
                >
                  Üye bağlantısını kaldır
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="liSimExpire"
                  onClick={expireLinkedInToken}
                >
                  Token süresini doldur
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="liSimRevoke"
                  onClick={revokeLinkedInPermission}
                >
                  İzni geri al
                </button>
              </div>
            ) : null}
            <details>
              <summary>{'Backend domain sözleşmesi (bu turda yalnız tanım)'}</summary>
              <div className="stack" style={{ gap: '8px', marginTop: '8px' }}>
                <div>
                  <b>CredentialLinkedInSync</b>
                  <pre className="code-block small">
                    {JSON.stringify(CREDENTIAL_LINKEDIN_SYNC_SHAPE, null, 2)}
                  </pre>
                </div>
                <div>
                  <b>LinkedInMemberConnection</b>
                  <pre className="code-block small">
                    {JSON.stringify(LINKEDIN_MEMBER_CONNECTION_SHAPE, null, 2)}
                  </pre>
                </div>
                <ul style={{ marginLeft: '18px' }}>
                  {LINKEDIN_CONTRACT_RULES.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            </details>
            <div className="help">
              <b>{'Resmî referanslar — uygulama anında yeniden doğrulanmalıdır:'}</b>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {LINKEDIN_REFERENCES.map((r) => (
                  <li key={r.url}>
                    <a href={r.url} target="_blank" rel="noopener">
                      {`${r.label} `}
                      <ExtIcon />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{'C · Public gizlilik'}</h2>
          </div>
          <div className="card-pad stack" style={{ gap: '10px' }}>
            <label className="check">
              <input
                type="checkbox"
                data-p="noindex"
                checked={bool(P, 'noindex')}
                onChange={(e) => togglePrivacy('noindex', e.target.checked)}
              />
              {' Public sayfalar arama motorlarında dizinlenmesin ('}
              <code>noindex</code>
              {', varsayılan açık)'}
            </label>
            {/* Locked, not hidden: these are product invariants the reference shows on purpose. */}
            <label className="check">
              <input type="checkbox" disabled defaultChecked={bool(P, 'publicSearch')} />
              {' Public liste / arama (kapalı — isim, e-posta veya şirket adına göre arama yoktur)'}
            </label>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' Onay bekleyen kayıt kişisel veri göstermez'}
            </label>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' Claim token public koddan ayrıdır (tek kullanımlık, süreli)'}
            </label>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' İptal edilen ve süresi dolan sayfalar silinmez (HTTP 200, durum gösterilir)'}
            </label>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' Bilinmeyen kod 404'}
            </label>
            <div className="help">
              <code>noindex</code>
              {
                " sayfayı gizlemez: bağlantıyı bilen herkes doğrulayabilir, LinkedIn'deki link çalışır; kişinin adıyla arama motorunda bulunması hedeflenmez."
              }
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{'D · Alıcı onayı (claim)'}</h2>
          </div>
          <div className="card-pad stack" style={{ gap: '12px' }}>
            <div className="field-row">
              <div className="field">
                <label htmlFor="cl_days">{'Claim token geçerlilik (gün)'}</label>
                <input
                  className="input"
                  id="cl_days"
                  type="number"
                  min="1"
                  max="365"
                  data-c="tokenValidityDays"
                  value={daysText}
                  onChange={(e) => {
                    setDaysText(e.target.value);
                    const n = Number.parseInt(e.target.value, 10) || days;
                    commands.patchLocal((d) => {
                      (d.settings.claim as Record<string, unknown>).tokenValidityDays = n;
                    });
                  }}
                />
              </div>
              <div className="field">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: the reference's caption for the wrapped checkbox that follows */}
                <label>Yeniden gönderim</label>
                <label className="check" style={{ marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    data-c="resendAllowed"
                    checked={bool(C, 'resendAllowed')}
                    onChange={(e) =>
                      commands.patchLocal((d) => {
                        (d.settings.claim as Record<string, unknown>).resendAllowed = e.target.checked;
                      })
                    }
                  />
                  {' Onay e-postası yeniden gönderilebilir'}
                </label>
              </div>
            </div>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' Tek kullanımlık: onaydan sonra bağlantı «kullanıldı» durumuna geçer'}
            </label>
            <label className="check">
              <input type="checkbox" disabled defaultChecked />
              {' Onay olmadan kayıt publicleşmez; public sayfa «henüz yayınlanmadı» gösterir'}
            </label>
            <div className="help">
              {"Üretimde token ≥128 bit rastgele üretilir, hash'lenerek saklanır ve süre dolunca yenilenir."}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{'E · E-posta domain durumu'}</h2>
          <span className="pill pill-info none">Simülasyon</span>
        </div>
        <div className="card-pad">
          <div className="summary-band" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
            <div className="item">
              <span>Gönderen domaini</span>
              <b style={{ fontSize: '15px' }}>{str(R, 'senderDomain')}</b>
            </div>
            <div className="item">
              <span>SPF</span>
              <ReadinessPill state={str(R, 'spf')} />
            </div>
            <div className="item">
              <span>DKIM</span>
              <ReadinessPill state={str(R, 'dkim')} />
            </div>
            <div className="item">
              <span>DMARC</span>
              <ReadinessPill state={str(R, 'dmarc')} />
            </div>
            <div className="item">
              <span>Delivery webhook</span>
              <ReadinessPill state={str(R, 'webhook')} />
            </div>
          </div>
          {/* API GAP: production reads these five from the Resend domain record and the webhook
              subscription; the fixture carries the reference's fixed sample values. */}
          <p className="help" style={{ marginTop: '10px' }}>
            {
              "Bu kartlar prototipte sabit örnek değerlerdir; üretimde Resend domain doğrulaması ve webhook kaydından okunur. DMARC kaydı «p=none» ile başlayıp «quarantine»'e geçmelidir."
            }
          </p>
        </div>
      </div>
      {/* Owner only, and strictly: the acceptance surface has no role and draws nothing here. */}
      {role === 'owner' ? <SheetSyncCard /> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {mayWrite ? (
          <button className="btn btn-primary" type="button" id="vfSave" onClick={() => void save()}>
            {/* The same form, the same button, and the truth about which write it is (PO 7.11 §5). */}
            {firstProfile ? 'Kurum profilini oluştur' : 'Kaydet'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------- the screen

export function SettingsScreen(): ReactElement {
  const { pathname } = useLocation();
  const role = useV07Role();
  /*
   * §6 places user management inside this structure rather than in the sidebar, and the
   * authorization matrix decides who sees it: `user.read` and `user.activate` are owner-ALLOW and
   * admin/operator/viewer-DENY (PO 7.5 §2.1, PO 7.6). No owner session, no third tab — which is
   * also the state the frozen prototype is in, so the tab row a reviewer compares is the
   * reference's own two entries.
   */
  const owner = role === 'owner';
  const tab =
    pathname.endsWith('/users') && owner
      ? 'users'
      : pathname.endsWith('/verification')
        ? 'verification'
        : 'email';

  return (
    <section data-screen="settings">
      <div className="page-head">
        <div>
          <span className="eyebrow">Ayarlar</span>
          <h1>E-posta ve Doğrulama</h1>
          <p className="lead">
            {'Gönderen kimliği, credential e-postası ve public doğrulama sayfasının yapılandırması.'}
          </p>
        </div>
      </div>
      <div className="tabs" id="settingsTabs">
        <Link to="/admin/settings/email" data-tab="email" className={tab === 'email' ? 'is-active' : ''}>
          E-posta şablonu
        </Link>
        <Link
          to="/admin/settings/verification"
          data-tab="verification"
          className={tab === 'verification' ? 'is-active' : ''}
        >
          {'Kurum kimliği, LinkedIn, gizlilik & claim'}
        </Link>
        {owner ? (
          <Link to="/admin/settings/users" data-tab="users" className={tab === 'users' ? 'is-active' : ''}>
            Kullanıcılar
          </Link>
        ) : null}
      </div>
      <div id="settingsBody">
        {tab === 'users' ? <SettingsUsersScreen /> : tab === 'email' ? <EmailTab /> : <VerificationTab />}
      </div>
    </section>
  );
}
